// Palette for the ePocus product — a separate app served from the /epocus route subtree and
// wrapped in its own EpocusThemeProvider. This is intentionally NOT part of the main app's design
// system (src/components/ + src/global-providers/mui-provider/). Don't import it into the main app,
// and don't add general-purpose colours here.

// Write font sizes in px, get accessible rem values.
// Requires html { font-size: 100% } (set by EpocusThemeProvider).
export const rem = (px: number) => `${px / 16}rem`;

// Primary CTA colour — aligns with PALETTE.primary.main from the wider app.
export const EPOCUS_PRIMARY = "#173B4F";
// Hover/pressed state for primary interactive elements
export const EPOCUS_PRIMARY_DARK = "#0F2E3E";
// Selected/tint background for primary-tinted interactive elements (exam tiles, option rows)
export const EPOCUS_SELECTED_BG = "#E4F1F1";
// Subtle hover background for primary-tinted interactive elements
export const EPOCUS_PRIMARY_HOVER_BG = "#F7FBFB";

// Active icon colour — PALETTE.brand.main, used for selected/active icon tinting.
export const EPOCUS_ICON_ACTIVE = "#2E9CA6";

export const EPOCUS_TEAL_DARK = "#1F7A82";
/** @deprecated Use EPOCUS_ICON_ACTIVE instead. */
export const EPOCUS_TEAL = EPOCUS_ICON_ACTIVE;
export const EPOCUS_BG = "#EAF4F4";
export const EPOCUS_BORDER = "#DCE7E8";
export const EPOCUS_INACTIVE = "#9AA7B2";
export const EPOCUS_WHITE = "#FFFFFF";
export const EPOCUS_TEXT_PRIMARY = "#1C2833";
export const EPOCUS_TEXT_SECONDARY = "#6B7A87";

export const EPOCUS_AMBER = "#E08A2B";
export const EPOCUS_AMBER_BG = "#FCF3E3";
export const EPOCUS_AMBER_BORDER = "#F0D9AE";
export const EPOCUS_GREEN = "#1E9E6A";
export const EPOCUS_GREEN_BG = "#E7F6EF";
export const EPOCUS_GREEN_BORDER = "#BEE6CF";
export const EPOCUS_BLUE = "#3B7DB0";
export const EPOCUS_BLUE_BG = "#E9F2F9";
export const EPOCUS_BLUE_BORDER = "#C3D9EA";
export const EPOCUS_RED = "#B23A3A";
export const EPOCUS_RED_BG = "#FBEAEA";
export const EPOCUS_RED_BORDER = "#EFC9C9";
export const EPOCUS_PURPLE = "#7A5CB4";
export const EPOCUS_PURPLE_BG = "#F0EAF8";
export const EPOCUS_NEUTRAL_BG = "#EEF2F6";
export const EPOCUS_NEUTRAL_BORDER = "#C2CFDB";

// Status badge colours
export const EPOCUS_STATUS_DRAFT_BG = EPOCUS_PURPLE_BG;
export const EPOCUS_STATUS_DRAFT_TEXT = EPOCUS_PURPLE;
export const EPOCUS_STATUS_PENDING_BG = EPOCUS_SELECTED_BG;
export const EPOCUS_STATUS_PENDING_TEXT = EPOCUS_TEAL_DARK;
export const EPOCUS_STATUS_REVIEWED_BG = EPOCUS_GREEN_BG;
export const EPOCUS_STATUS_REVIEWED_TEXT = EPOCUS_GREEN;
export const EPOCUS_STATUS_COMPLETE_BG = EPOCUS_NEUTRAL_BG;
export const EPOCUS_STATUS_COMPLETE_TEXT = EPOCUS_TEXT_SECONDARY;
export const EPOCUS_STATUS_IMPORTED_BG = EPOCUS_AMBER_BG;
export const EPOCUS_STATUS_IMPORTED_TEXT = EPOCUS_AMBER;

export const EPOCUS_PANEL_SCOPED_BORDER = "#B9CEDF";
export const EPOCUS_PANEL_SCOPED_HEADER_BG = EPOCUS_SELECTED_BG;
export const EPOCUS_PANEL_MUTED_BG = EPOCUS_NEUTRAL_BG;
export const EPOCUS_PANEL_MUTED_BORDER = EPOCUS_NEUTRAL_BORDER;

// To Document card
export const EPOCUS_TO_DOC_BG = EPOCUS_AMBER_BG;
export const EPOCUS_TO_DOC_BORDER = EPOCUS_AMBER_BORDER;
export const EPOCUS_ORANGE = EPOCUS_AMBER;
export const EPOCUS_ORANGE_HOVER = "#C7761F";

export const EPOCUS_TO_PERFORM_BG = EPOCUS_BLUE_BG;
export const EPOCUS_TO_PERFORM_BORDER = EPOCUS_BLUE_BORDER;
export const EPOCUS_STATUS_ORDERED_BG = EPOCUS_BLUE_BG;
export const EPOCUS_STATUS_ORDERED_TEXT = EPOCUS_BLUE;
export const EPOCUS_URGENT_BG = EPOCUS_RED_BG;
export const EPOCUS_URGENT_TEXT = EPOCUS_RED;
export const EPOCUS_URGENT_BORDER = EPOCUS_RED_BORDER;

// Synced badge
export const EPOCUS_SYNCED_BG = EPOCUS_GREEN_BG;
export const EPOCUS_SYNCED_TEXT = EPOCUS_GREEN;

export const EPOCUS_NAVY = "#173B4F";

export const EPOCUS_SCAN_SURFACE = "#0C1826";
export const EPOCUS_SCAN_TRAY = "#0A141F";
export const EPOCUS_SCAN_THUMB = "#16283C";
export const EPOCUS_SCAN_BADGE_BG = "rgba(12,24,38,0.7)";
export const EPOCUS_SCAN_BADGE_BORDER = "rgba(255,255,255,0.14)";
export const EPOCUS_ON_DARK_PRIMARY = "#B9CFE6";
export const EPOCUS_ON_DARK_MUTED = "#7E93A8";

export const EPOCUS_SUCCESS = EPOCUS_GREEN;
export const EPOCUS_SUCCESS_DARK = "#178A5B";
export const EPOCUS_SUCCESS_BG = EPOCUS_GREEN_BG;

export const EPOCUS_FONT_FAMILY =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export const EPOCUS_RADIUS_CARD = 14;
export const EPOCUS_RADIUS_BUTTON = 9;
export const EPOCUS_RADIUS_NAV = 10;
export const EPOCUS_RADIUS_PILL = 20;

export const EPOCUS_CARD_SHADOW =
  "0 1px 3px rgba(23,59,79,0.06), 0 1px 2px rgba(23,59,79,0.04)";

export const EPOCUS_HEADING_STYLE = {
  fontSize: rem(28),
  fontWeight: 700,
  letterSpacing: "-0.02em",
  color: EPOCUS_TEXT_PRIMARY,
} as const;

export const EPOCUS_KPI_VALUE_STYLE = {
  fontSize: rem(30),
  fontWeight: 700,
  lineHeight: 1,
  color: EPOCUS_TEXT_PRIMARY,
} as const;

export const EPOCUS_KPI_LABEL_STYLE = {
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
} as const;
